/**
 * Credlix Unified Simulation & Agent SDK
 * Handles AI Workflows, Simulations, and Live Agent Handoffs.
 */
class CredlixAgent {
    constructor(config) {
        this.config = config;
        this.baseUrl = (config.baseUrl || window.location.origin).replace(/\/$/, "");
        this.apiKey = config.apiKey;
        this.apiSecret = config.apiSecret;
        this.token = config.token;

        this.workflowId = config.workflowId;
        this.outboundWorkflowId = config.outboundWorkflowId || config.workflowId;

        // Target (Customer) Properties
        this.userName = config.userName || "SDK Tester";
        this.phoneNumber = config.phoneNumber || "+919999999999";

        // Agent Properties
        this.agentName = config.agentName || "Agent";
        this.agentPhone = config.agentPhone || "+910000000000";
        this.agentEmail = config.agentEmail || "agent@example.com";

        // Real Agent Connection Options
        this.connectWithRealAgent = (config && config.connectWithRealAgent !== undefined) ? config.connectWithRealAgent : true;
        this.connectToMeOnly = (config && config.connectToMeOnly !== undefined) ? config.connectToMeOnly : true;

        // [IDENTITY] Derive agent identity from the JWT so the SDK never sends a
        // client-claimed email/company_id that could mismatch the server's decode.
        // The server already derives routing identity from the same token; mirroring
        // it here keeps the agent_ready payload consistent.
        this._tokenClaims = this._decodeToken(this.token);
        if (this._tokenClaims && this._tokenClaims.email) {
            this.agentEmail = this._tokenClaims.email;
        }
        if (this._tokenClaims && this._tokenClaims.company_id) {
            this.companyId = this._tokenClaims.company_id;
        }

        this.chatTitle = config.chatTitle || "Voxera Support";
        this.callBtnText = config.callBtnText || config.callMeBtnText || "Talk to AI";

        // Headless Mode & Callbacks
        this.renderWidget = config.renderWidget !== false;
        this.showNotifications = config.showNotifications !== false;
        this.showFloatingButton = config.showFloatingButton !== false; // Users can hide the bottom-right button
        this.allowButtonHide = config.allowButtonHide !== false;        // Enable the × affordance on the button
        this.onStateChange = config.onStateChange || null;
        this.onIncomingCall = config.onIncomingCall || null; // For agent mode
        this.onError = config.onError || ((type, err) => console.error(`[Credlix SDK] ${type}:`, err));

        // Initialization
        this.callId = null;
        this.companyId = null;
        this.socket = null; // High-level socket (AI Stream or Agent Bridge)
        this.availabilitySocket = null; // Low-level signaling socket

        // Audio State
        this.audioContext = null;
        this.micStream = null;
        this.processor = null;
        this.nextStartTime = 0;

        this.container = null;
        this._initUI();

        // Audio State Extensions
        this.isMuted = false;

        // Heartbeat
        this.heartbeatInterval = 30000;
        this.heartbeatTimer = null;

        // Ringer & Dialing Context
        this.ringAudioCtx = null;
        this.ringerInterval = null;
        this.dialingAudio = null;

        // [STABILITY] Performance & Cleanup Tracking
        this._lastAudioTime = 0;
        this._uiMonitor = null;
        this._diagnosticThrottle = 0;

        if (typeof window !== 'undefined' && window.document) {
            document.body.addEventListener('click', () => {
                if (!this.ringAudioCtx) {
                    this.ringAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
                }
            }, { once: true });
        }

        // =========================================================
        // CUSTOM RINGTONE OVERRIDE (PRODUCTION SAFE)
        // =========================================================
        try {
            if (typeof window !== 'undefined' && typeof Audio !== 'undefined') {
                const customRingtone = new Audio('/assets/sounds/dialing.mp3');
                customRingtone.loop = true;
                // Save original methods
                const sdk = this.sdk || this;
                const originalStopRingSound = sdk.stopRingSound.bind(sdk);
                const originalPlayRingSound = sdk.playRingSound.bind(sdk);

                sdk.playRingSound = () => {
                    sdk.stopRingSound();
                    customRingtone.play().catch(err => {
                        if (err.name === 'AbortError') {
                            // console.log('[Dialer] Ringtone aborted properly because call connected.');
                            return;
                        }
                        // console.warn('[Dialer] Custom ringtone blocked, falling back to default:', err);
                        originalPlayRingSound();
                    });
                };

                sdk.stopRingSound = () => {
                    if (customRingtone) {
                        customRingtone.pause();
                        customRingtone.currentTime = 0;
                    }
                    originalStopRingSound();
                };
            }
        } catch (audioErr) {
            console.warn('[Dialer] Failed to inject custom ringtone.', audioErr);
        }
    }

    // -------------------------------------------------------------------------
    // Core AI / Simulation Logic
    // -------------------------------------------------------------------------

    async startCall() {
        try {
            this._updateState("connecting");
            await this._getToken();
            await this._initSimulation();
            await this._setupAudio(false); // Standard AI stream

            const wsProto = window.location.protocol === "https:" ? "wss:" : "wss:";
            const domain = this.baseUrl.split("://")[1];
            const wsUrl = `${wsProto}//${domain}/api/v1/webhooks/stream/${this.callId}/${this.companyId}/inbound`;

            this.socket = new WebSocket(wsUrl);
            this.socket.onopen = () => {
                this.socket.send(JSON.stringify({ type: "start" }));
                this._updateState("active");
                this.nextStartTime = this.audioContext.currentTime;
            };

            this.socket.onmessage = async (event) => {
                const data = JSON.parse(event.data);
                if (data.type === "media" && data.payload) {
                    this.stopDialingSound();
                    this._playAudio(data.payload);
                }
            };

            this.socket.onclose = () => this.stopCall();
            this.socket.onerror = () => this.stopCall();

        } catch (e) {
            this.stopDialingSound();
            this.log(e.message);
            this.notify("Call Error", e.message, "error");
            this._updateState("idle");
            this.onError("init_failed", e);
        }
    }

    async initiateOutboundCall(
        phoneNumber = this.phoneNumber,
        name = this.userName,
        workflowId = null,
        language = "English",
        requestId = null,
        context = {},
        connectWithRealAgent = this.connectWithRealAgent,
        connectToMeOnly = this.connectToMeOnly
    ) {
        // Retrieve form data from sessionStorage if available
        let sessionData = {};
        if (typeof window !== "undefined" && window.sessionStorage) {
            try {
                const stored = sessionStorage.getItem("formDataTalkToRM");
                if (stored) {
                    sessionData = typeof stored === "string" ? JSON.parse(stored) : stored;
                }
            } catch (err) {
                console.warn("[CredlixAgent] Error reading formDataTalkToRM from sessionStorage:", err);
            }
        }
        const sessionName = sessionData?.name || sessionData?.Name || "";
        const sessionPhone = sessionData?.phoneNumber || sessionData?.phone || sessionData?.mobile || sessionData?.mobileNumber || "";
        const sessionEmail = sessionData?.email || sessionData?.Email || "";

        const isRealAgent = connectWithRealAgent !== undefined ? connectWithRealAgent : (this.connectWithRealAgent !== undefined ? this.connectWithRealAgent : true);
        const isToMeOnly = connectToMeOnly !== undefined ? connectToMeOnly : (this.connectToMeOnly !== undefined ? this.connectToMeOnly : true);
        const targetPhone = phoneNumber || this.phoneNumber;
        const targetName = name || this.userName;

        try {
            if (isRealAgent) {
                this._currentIncomingData = { customer_name: targetName, phone_number: targetPhone };
                this._showConnectingModal(targetPhone, targetName);
            }
            if (isRealAgent && !this.availabilitySocket) {
                this.log("Auto-online triggered for real agent connection...");
                await this.goOnline();
            }

            // this.log(`Triggering outbound call to ${targetPhone} (connectWithRealAgent: ${isRealAgent}, connectToMeOnly: ${isToMeOnly}, agent_phone: ${this.agentPhone})...`);
            await this._getToken();

            const endpoint = isToMeOnly
                ? `${this.baseUrl}/api/v1/call/dialer/call-now`
                : `${this.baseUrl}/api/v1/call`;

            const res = await fetch(endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${this.token}`
                },
                body: JSON.stringify({
                    phone_number: targetPhone,
                    name: targetName,
                    target_group_id: workflowId || this.outboundWorkflowId,
                    direction: "outbound",
                    language: language,
                    request_id: requestId,
                    context: {
                        ...context,
                        end_user_customer_name: sessionName,
                        end_user_customer_number: sessionPhone,
                        end_user_customer_email: sessionEmail,
                        is_talk_to_rm: true
                    },
                    connectWithRealAgent: isRealAgent,
                    connectToMeOnly: isToMeOnly,
                    agent_phone: isRealAgent ? this.agentPhone : null
                })
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || "Failed to trigger outbound call");

            this.log(`Outbound call triggered: ${data.call_id}`);

            // [FIX] For connectToMeOnly calls, store pending call_id so we can auto-connect
            // when we receive the incoming_handoff notification (no popup/answer needed)
            if (isToMeOnly && isRealAgent) {
                this._pendingDirectPatchCallId = data.call_id;
                this.log(`[DIRECT-PATCH] Waiting for bridge notification for call ${data.call_id}`);
            }

            if (!isRealAgent) {
                this.notify("Call Placed", "The outbound call has been initiated successfully.", "success");
            }
            return data;
        } catch (e) {
            this._hideConnectingModal();
            this._hideActiveCockpit();
            this._pendingDirectPatchCallId = null;
            this.log(`Outbound Error: ${e.message}`);
            if (!isRealAgent) {
                this.notify("Call Failed", e.message, "error");
            }
            this.onError("outbound_failed", e);
            throw e;
        }
    }

    stopCall() {
        // [GUARD] Prevent multiple stopCall executions
        if (this._isStopping) {
            console.log("[CredlixAgent] stopCall already in progress, skipping duplicate.");
            return;
        }
        this._isStopping = true;

        // console.log("[CredlixAgent] stopCall initiated, current state:", this.socket ? this.socket.readyState : 'no socket');

        try {
            // [UX] Force immediate UI cleanup of all possible overlays
            this._hideConnectingModal();
            this._hideRingModal();
            this._hideActiveCockpit();
            this.stopRingSound();
            this.stopDialingSound();

            if (this.socket) {
                try { this.socket.send(JSON.stringify({ type: "hangup" })); } catch (e) { }
                try { this.socket.close(); } catch (e) { }
                this.socket = null;
            }

            this._stopAudio();
            this._stopUIMonitor();
            this._updateState("idle");

            // [FIX] Re-register availability after call ends to re-enter the routing pool.
            // If the availability socket is closed/not open, reconnect automatically.
            if (this.availabilitySocket && this.availabilitySocket.readyState === WebSocket.OPEN) {
                this.availabilitySocket.send(JSON.stringify({
                    type: "agent_ready",
                    agent_name: this.agentName,
                    agent_phone: this.agentPhone,
                    agent_email: this.agentEmail,
                    company_id: this.companyId
                }));
            } else {
                this._reconnectAvailability();
            }
        } catch (err) {
            console.error("[CredlixAgent] Error during stopCall:", err);
            // Force reset state even if error occurs
            this._updateState("idle");
        } finally {
            this._isStopping = false;
        }
    }

    async _reconnectAvailability() {
        // Prevent multiple concurrent reconnection attempts
        if (this._isReconnecting) {
            console.log("[CredlixAgent] Already reconnecting, skipping duplicate attempt.");
            return;
        }
        this._isReconnecting = true;

        // Close stale socket if exists
        if (this.availabilitySocket) {
            try { this.availabilitySocket.close(); } catch (e) { }
            this.availabilitySocket = null;
        }
        this._stopHeartbeat();

        // Re-establish availability connection
        try {
            await this.goOnline();
        } catch (e) {
            console.error("[CredlixAgent] Failed to reconnect availability socket:", e);
            // Retry after delay
            setTimeout(() => {
                this._isReconnecting = false;
                if (!this.availabilitySocket || this.availabilitySocket.readyState !== WebSocket.OPEN) {
                    console.log("[CredlixAgent] Retrying availability reconnection...");
                    this._reconnectAvailability();
                }
            }, 3000);
            return;
        }
        this._isReconnecting = false;
    }
    // -------------------------------------------------------------------------
    // Agent Connectivity, Handoff & Media Controls
    // -------------------------------------------------------------------------

    mute() {
        if (this.micStream) {
            this.micStream.getAudioTracks().forEach(track => track.enabled = false);
            this.isMuted = true;
            this._updateMuteUI();
            this.log("Microphone muted.");
        }
    }

    unmute() {
        if (this.micStream) {
            this.micStream.getAudioTracks().forEach(track => track.enabled = true);
            this.isMuted = false;
            this._updateMuteUI();
            this.log("Microphone unmuted.");
        }
    }

    _updateMuteUI() {
        const btn = this.elements ? this.elements.cockpitMuteBtn : null;
        if (btn) {
            if (this.isMuted) {
                btn.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/><line x1="1" y1="1" x2="23" y2="23"/></svg> Unmute`;
                btn.classList.add("muted");
            } else {
                btn.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg> Mute`;
                btn.classList.remove("muted");
            }
        }
    }

    playRingSound() {
        this.stopRingSound(); // [FIX] Prevent duplicate intervals/leaks
        if (!this.ringAudioCtx) this.ringAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
        if (this.ringAudioCtx.state === 'suspended') this.ringAudioCtx.resume();

        const beep = () => {
            if (this.ringAudioCtx.state === 'suspended') return;
            const osc = this.ringAudioCtx.createOscillator();
            const gain = this.ringAudioCtx.createGain();
            osc.connect(gain);
            gain.connect(this.ringAudioCtx.destination);

            osc.type = "sine";
            osc.frequency.setValueAtTime(440, this.ringAudioCtx.currentTime);
            osc.frequency.setValueAtTime(480, this.ringAudioCtx.currentTime + 0.1);

            gain.gain.setValueAtTime(0, this.ringAudioCtx.currentTime);
            gain.gain.linearRampToValueAtTime(0.5, this.ringAudioCtx.currentTime + 0.05);
            gain.gain.exponentialRampToValueAtTime(0.01, this.ringAudioCtx.currentTime + 0.5);

            osc.start();
            osc.stop(this.ringAudioCtx.currentTime + 0.5);
        };

        beep();
        this.ringerInterval = setInterval(beep, 2000);
    }

    stopRingSound() {
        if (this.ringerInterval) {
            clearInterval(this.ringerInterval);
            this.ringerInterval = null;
        }
        // [FIX] Explicitly close and nullify Ring Audio Context to release resources
        if (this.ringAudioCtx) {
            try {
                this.ringAudioCtx.close();
            } catch (e) {
                console.warn("[SDK] Error closing ring audio context:", e);
            }
            this.ringAudioCtx = null;
        }
    }

    playDialingSound() {
        try {
            if (typeof window !== 'undefined' && typeof Audio !== 'undefined') {
                if (!this.dialingAudio) {
                    this.dialingAudio = new Audio('/assets/sounds/dialing.mp3');
                    this.dialingAudio.loop = true;
                }
                this.dialingAudio.currentTime = 0;
                const playPromise = this.dialingAudio.play();
                if (playPromise !== undefined) {
                    playPromise.catch(err => {
                        if (err.name !== 'AbortError') {
                            console.warn('[CredlixAgent] Dialing audio playback was prevented:', err);
                        }
                    });
                }
            }
        } catch (e) {
            console.warn('[CredlixAgent] Failed to play dialing sound:', e);
        }
    }

    stopDialingSound() {
        try {
            if (this.dialingAudio) {
                this.dialingAudio.pause();
                this.dialingAudio.currentTime = 0;
            }
        } catch (e) {
            console.warn('[CredlixAgent] Failed to stop dialing sound:', e);
        }
    }

    async goOnline() {
        this._isExplicitlyOffline = false;
        if (this.availabilitySocket) {
            if (this.availabilitySocket.readyState === WebSocket.OPEN) {
                // console.log("[CredlixAgent] Already online");
                return;
            }
            // Socket exists but not open - wait briefly or force close
            if (this.availabilitySocket.readyState === WebSocket.CONNECTING) {
                // console.log("[CredlixAgent] Socket still connecting, waiting...");
                // Wait up to 3 seconds for connection
                await new Promise(resolve => setTimeout(resolve, 3000));
                if (this.availabilitySocket && this.availabilitySocket.readyState === WebSocket.OPEN) {
                    return;
                }
            }
            // Force close if still not open
            try { this.availabilitySocket.close(); } catch (e) { }
            this.availabilitySocket = null;
        }

        await this._getToken();

        const wsProto = window.location.protocol === "https:" ? "wss:" : "wss:";
        const domain = this.baseUrl.split("://")[1];
        // [IDENTITY] Send JWT so backend can derive a trusted routing identity
        // (email or api-key client_id). This lets call-now route the call to THIS
        // agent's browser — works cross-domain and for both user and api-key tokens.
        let wsUrl = `${wsProto}//${domain}/api/v1/webhooks/agent-availability`;
        if (this.token) {
            wsUrl += `?token=${encodeURIComponent(this.token)}`;
        }

        // console.log("[CredlixAgent] Connecting availability socket to:", wsUrl);
        this.availabilitySocket = new WebSocket(wsUrl);

        this.availabilitySocket.onopen = () => {
            // if (this.availabilitySocket && this.availabilitySocket.readyState === WebSocket.OPEN) {
            this.availabilitySocket.send(JSON.stringify({
                type: "agent_ready",
                agent_name: this.agentName,
                agent_phone: this.agentPhone,
                agent_email: this.agentEmail,
                company_id: this.companyId
            }));
            // }
            this._startHeartbeat();
            if (this.onStateChange) this.onStateChange("online");
        };

        this.availabilitySocket.onmessage = (e) => {
            const data = JSON.parse(e.data);
            if (data.type === "incoming_handoff" || data.type === "incoming_call") {
                this.log(`Incoming ${data.type} for call ${data.call_id}`);

                // [FIX] Direct Patch: If this is our own outbound call (connectToMeOnly),
                // auto-connect to agent-channel without showing popup/answer button
                if (this._pendingDirectPatchCallId && (data.call_id === this._pendingDirectPatchCallId || data.is_direct_patch)) {
                    this.log(`[DIRECT-PATCH] Auto-connecting to agent-channel for ${data.call_id}`);
                    this._pendingDirectPatchCallId = null;
                    this._hideConnectingModal();
                    this.stopRingSound();
                    this.startBridge(data.call_id);
                    return;
                }

                this.playRingSound();
                this._showIncomingCallPopup(data);
                if (this.onIncomingCall) {
                    this.onIncomingCall(data);
                }
            } else if (data.type === "answer_handoff_ok") {
                this.stopRingSound();
                this._hideRingModal();
                this.startBridge(data.call_id);
            } else if (data.type === "answer_handoff_failed") {
                this.stopRingSound();
                this._hideRingModal();
                this.notify("Handoff Refused", data.reason || "Call was claimed by another agent.", "error");
            } else if (data.type === "handoff_cancelled" || data.type === "call_ended") {
                this.stopRingSound();
                this._hideRingModal();
            }
        };

        this.availabilitySocket.onclose = () => {
            this.availabilitySocket = null;
            this._stopHeartbeat();
            if (this.onStateChange) this.onStateChange("offline");

            // [FIX] Auto-reconnect availability socket on unexpected disconnect
            // (e.g., server restart, network glitch, backend cleanup)
            // Don't reconnect if user explicitly went offline via goOffline()
            if (!this._isExplicitlyOffline && !this._isReconnecting) {
                // console.log("[CredlixAgent] Scheduling availability reconnect in 2s...");
                setTimeout(() => this._reconnectAvailability(), 2000);
            }
        };
    }

    goOffline() {
        this._isExplicitlyOffline = true;
        if (this.availabilitySocket) {
            this.availabilitySocket.close();
        }
    }

    async answerHandoff(callId) {
        this.stopRingSound(); // [FIX] Proactive stop immediately on button click
        if (!this.availabilitySocket || this.availabilitySocket.readyState !== WebSocket.OPEN) {
            throw new Error("Cannot answer: Agent is offline.");
        }
        this.availabilitySocket.send(JSON.stringify({
            type: "answer_handoff",
            call_id: callId
        }));
    }

    async startBridge(callId) {
        this.callId = callId;
        const wsProto = window.location.protocol === "https:" ? "wss:" : "wss:";
        const domain = this.baseUrl.split("://")[1];
        const wsUrl = `${wsProto}//${domain}/api/v1/webhooks/agent-channel/${callId}`;

        await this._setupAudio(true); // Agent use pcm16 8k

        this.socket = new WebSocket(wsUrl);
        this.socket.onopen = async () => {
            // [FIX] Double-ensure context is running on user activation
            if (this.audioContext && this.audioContext.state !== "running") {
                await this.audioContext.resume();
            }
            this.socket.send(JSON.stringify({ type: "answer_call", call_id: callId }));
            this._updateState("active");
            this.nextStartTime = this.audioContext.currentTime;

            // [STABILITY] Start single UI monitor instead of thousands of setTimeouts
            this._startUIMonitor();

            // [UX] Morph into Active Cockpit instead of closing the modal
            const name = this._currentIncomingData ? (this._currentIncomingData.customer_name || this._currentIncomingData.phone_number) : "Customer";
            this._showActiveCockpit(name, "");
        };

        this.socket.onmessage = (e) => {
            const data = JSON.parse(e.data);
            if (data.type === "audio") {
                this._playAudio(data.audio_data);
            } else if (data.type === "call_ended" || data.type === "call_fallback_ai") {
                this.stopCall();
            }
        };

        this.socket.onclose = () => this.stopCall();
    }

    // -------------------------------------------------------------------------
    // Audio Persistence & Jitter Buffering
    // -------------------------------------------------------------------------

    _setupAudio(isAgent = false) {
        // [FIX] Use Native Rate: Creating an 8kHz context directly can cause
        // low volume/clicks in some browsers. Use native rate and resample manually.
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        this.micStream = null;
        this._resampleState = { index: 0 };

        // [FIX] Return the Promise so callers can await it properly
        return navigator.mediaDevices.getUserMedia({
            audio: {
                echoCancellation: { ideal: true },
                noiseSuppression: { ideal: true },
                autoGainControl: { ideal: true },
                googEchoCancellation: true,
                googNoiseSuppression: true,
                googAutoGainControl: true,
                googHighpassFilter: true,
                googAudioMirroring: false,
                googDucking: false,
            }
        }).then(stream => {
            this.micStream = stream;
            const source = this.audioContext.createMediaStreamSource(this.micStream);

            // [FIX] Use AudioWorklet if available, fall back to ScriptProcessor
            // ScriptProcessor is deprecated but still works in most browsers
            if (this.audioContext.audioWorklet) {
                return this._setupAudioWorklet(source, isAgent).catch(err => {
                    console.warn("[SDK] AudioWorklet setup failed, falling back to ScriptProcessor:", err);
                    this._setupScriptProcessor(source, isAgent);
                });
            } else {
                this._setupScriptProcessor(source, isAgent);
            }
        }).catch(err => {
            console.error("[SDK] getUserMedia failed:", err);
            this.notify("Microphone Error", "Could not access microphone. Please check permissions.", "error");
            this.onError("mic_access_failed", err);
            throw err; // Re-throw so callers know setup failed
        });
    }

    _setupScriptProcessor(source, isAgent) {
        try {
            // [FIX] Create a ScriptProcessor with explicit output connection
            // ScriptProcessor requires connection to destination to process audio in some browsers
            const bufferSize = 4096;
            this.processor = this.audioContext.createScriptProcessor(bufferSize, 1, 1);
            source.connect(this.processor);

            // [CRITICAL FIX] Connect to a silent gain node instead of destination
            // This ensures audio processing works without feedback
            const silentGain = this.audioContext.createGain();
            silentGain.gain.value = 0; // Mute output to speakers
            this.processor.connect(silentGain);
            silentGain.connect(this.audioContext.destination);

            // [DEBUG] Track audio processing
            let audioPacketCount = 0;
            let lastSendTime = Date.now();

            this.processor.onaudioprocess = (e) => {
                audioPacketCount++;

                // Check if socket is ready
                if (!this.socket) {
                    if (audioPacketCount <= 3) console.warn("[SDK-AUDIO] Socket is null, skipping");
                    return;
                }
                if (this.socket.readyState !== WebSocket.OPEN) {
                    if (audioPacketCount <= 3) console.warn(`[SDK-AUDIO] Socket state=${this.socket.readyState}, skipping`);
                    return;
                }

                const input = e.inputBuffer.getChannelData(0);

                // Calculate audio level for debugging
                let maxVal = 0;
                for (let i = 0; i < input.length; i++) {
                    const abs = Math.abs(input[i]);
                    if (abs > maxVal) maxVal = abs;
                }

                // Log audio levels periodically
                if (audioPacketCount <= 10 || audioPacketCount % 200 === 0) {
                    console.log(`[SDK-AUDIO] Packet #${audioPacketCount}, inputLevel=${maxVal.toFixed(4)}, socketReady=${this.socket.readyState === WebSocket.OPEN}`);
                }

                // [FIX] High-Precision Stateful Resampling
                const resampled = this._resampleTo8kHz(input, this.audioContext.sampleRate);
                const pcm16 = this._floatTo16BitPCM(resampled);
                const base64 = btoa(String.fromCharCode(...new Uint8Array(pcm16.buffer)));

                // Send audio packet
                this.socket.send(JSON.stringify({
                    type: isAgent ? "audio" : "media",
                    audio_data: isAgent ? base64 : undefined,
                    payload: isAgent ? undefined : base64,
                    timestamp: Date.now(),
                    sample_rate: 8000
                }));

                if (audioPacketCount <= 5) {
                    console.log(`[SDK-AUDIO] Sent packet #${audioPacketCount}, ${base64.length} bytes, level=${maxVal.toFixed(4)}`);
                }
            };

            // console.log("[SDK] Audio setup complete (ScriptProcessor), micStream active:", this.micStream ? this.micStream.active : false);
            // console.log("[SDK] AudioContext state:", this.audioContext.state, "sampleRate:", this.audioContext.sampleRate);
        } catch (err) {
            console.error("[SDK] ScriptProcessor setup failed:", err);
            this.notify("Audio Error", "Failed to initialize audio processing.", "error");
        }
    }

    async _setupAudioWorklet(source, isAgent) {
        try {
            // AudioWorklet is the modern replacement for ScriptProcessor
            // For now, fall back to ScriptProcessor since we don't have a worklet processor defined
            throw new Error("AudioWorklet processor not implemented, using ScriptProcessor");
        } catch (err) {
            throw err;
        }
    }

    _resampleTo8kHz(input, fromRate) {
        const targetRate = 8000;
        const ratio = fromRate / targetRate;

        let i = this._resampleState.index;
        const output = [];

        while (i < input.length) {
            const i1 = Math.floor(i);
            const i2 = Math.min(input.length - 1, i1 + 1);
            const t = i - i1;
            output.push(input[i1] * (1 - t) + input[i2] * t);
            i += ratio;
        }

        // Carry over the fractional index to the next chunk
        this._resampleState.index = i - input.length;
        return new Float32Array(output);
    }

    _floatTo16BitPCM(input) {
        const output = new Int16Array(input.length);
        for (let i = 0; i < input.length; i++) {
            const s = Math.max(-1, Math.min(1, input[i]));
            output[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        return output;
    }

    async _playAudio(base64) {
        this.stopDialingSound();
        if (!this.audioContext) return;
        if (this.audioContext.state === "suspended") await this.audioContext.resume();

        try {
            // [EMERGENCY FIX] Restoring the missing decoding logic
            const binary = atob(base64);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

            const pcm16 = new Int16Array(bytes.buffer, bytes.byteOffset, bytes.length / 2);
            const float32 = new Float32Array(pcm16.length);
            for (let i = 0; i < pcm16.length; i++) float32[i] = pcm16[i] / 32768.0;

            const audioBuffer = this.audioContext.createBuffer(1, float32.length, 8000);
            audioBuffer.getChannelData(0).set(float32);

            const source = this.audioContext.createBufferSource();
            source.buffer = audioBuffer;
            source.connect(this.audioContext.destination);

            const currentTime = this.audioContext.currentTime;
            if (this.nextStartTime < currentTime) {
                this.nextStartTime = currentTime + 0.050; // 50ms stable buffer
            }

            source.start(this.nextStartTime);
            this.nextStartTime += audioBuffer.duration;

            this._lastAudioTime = Date.now();
            if (this.renderWidget && this.widget.iconWrapper) {
                this.widget.iconWrapper.classList.add("speaking");
            }
        } catch (e) {
            console.error('[SDK-DIAG] Error playing audio chunk:', e);
        }
    }

    _startUIMonitor() {
        this._stopUIMonitor();
        this._uiMonitor = setInterval(() => {
            // Remove 'speaking' animation if no audio for > 500ms
            if (Date.now() - this._lastAudioTime > 500) {
                if (this.renderWidget && this.widget.iconWrapper) {
                    this.widget.iconWrapper.classList.remove("speaking");
                }
            }
        }, 200);
    }

    _stopUIMonitor() {
        if (this._uiMonitor) {
            clearInterval(this._uiMonitor);
            this._uiMonitor = null;
        }
        if (this.renderWidget && this.widget.iconWrapper) {
            this.widget.iconWrapper.classList.remove("speaking");
        }
    }

    _stopAudio() {
        if (this.processor) this.processor.disconnect();
        if (this.micStream) this.micStream.getTracks().forEach(t => t.stop());
        if (this.audioContext) this.audioContext.close();

        this.processor = null;
        this.micStream = null;
        this.audioContext = null;
    }

    // -------------------------------------------------------------------------
    // UI - Simulation Widget & Popups
    // -------------------------------------------------------------------------

    _initUI() {
        // [FIX] Prevent multiple widget instances — remove stale widget if re-initialized
        const existing = document.getElementById("credlix-agent-widget");
        if (existing) existing.remove();

        const container = document.createElement("div");
        container.id = "credlix-agent-widget";
        const shadow = container.attachShadow({ mode: "open" });

        const style = document.createElement("style");
        style.textContent = `
            :host { --primary: #6366f1; --primary-hover: #4338ca; --bg: rgba(15, 23, 42, 0.98); --text: #ffffff; }
            .widget-toggle { position: fixed; bottom: 24px; right: 24px; width: 64px; height: 64px; border-radius: 50%; background: var(--primary); box-shadow: 0 10px 25px rgba(0,0,0,0.5); cursor: pointer; display: flex; align-items: center; justify-content: center; transition: transform 0.3s ease, opacity 0.3s ease; z-index: 10000; }
            .widget-toggle svg { width: 30px; height: 30px; fill: white; }
            /* [UX] Floating button hide/show controls */
            .widget-toggle.hidden { opacity: 0; pointer-events: none; transform: scale(0.5); }
            .widget-toggle .toggle-hide-btn { position: absolute; top: -6px; right: -6px; width: 22px; height: 22px; border-radius: 50%; background: rgba(0,0,0,0.6); color: #fff; font-size: 14px; line-height: 22px; text-align: center; cursor: pointer; opacity: 0; transition: opacity 0.2s; z-index: 10001; }
            .widget-toggle:hover .toggle-hide-btn { opacity: 1; }
            .widget-toggle .toggle-hide-btn:hover { background: rgba(239, 68, 68, 0.9); }
            .widget-show-btn { position: fixed; bottom: 24px; right: 24px; width: 36px; height: 36px; border-radius: 50%; background: var(--primary); box-shadow: 0 4px 12px rgba(0,0,0,0.3); cursor: pointer; display: none; align-items: center; justify-content: center; z-index: 10000; transition: transform 0.2s; }
            .widget-show-btn:hover { transform: scale(1.1); }
            .widget-show-btn.visible { display: flex; }
            .widget-show-btn svg { width: 18px; height: 18px; fill: white; }
            .panel-overlay { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(15, 23, 42, 0.7); backdrop-filter: blur(4px); z-index: 99999; display: none; align-items: center; justify-content: center; padding: 16px; box-sizing: border-box; }
            .panel-overlay.visible { display: flex; }
            .panel { position: relative; width: 360px; max-width: 100%; height: 520px; max-height: 90vh; background: var(--bg); border-radius: 20px; box-shadow: 0 25px 50px rgba(0,0,0,0.5); display: flex; flex-direction: column; overflow: hidden; border: 1px solid rgba(255,255,255,0.1); font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
            .header { padding: 24px 20px; background: var(--primary); color: white; display: flex; justify-content: space-between; align-items: center; }
            .header h3 { margin: 0; font-size: 18px; font-weight: 600; font-family: sans-serif; }
            .body { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 40px 20px; background: radial-gradient(circle at top, rgba(30, 41, 59, 1), rgba(15, 23, 42, 1)); }
            .status-indicator { margin-bottom: 40px; display: flex; align-items: center; font-family: sans-serif; font-size: 14px; color: rgba(255, 255, 255, 0.6); }
            .status-dot { width: 10px; height: 10px; border-radius: 50%; background: rgba(255,255,255,0.2); margin-right: 10px; }
            .status-dot.active { background: #10b981; animation: pulse-dot 2s infinite; }
            .call-animation { width: 140px; height: 140px; border-radius: 50%; background: rgba(99, 102, 241, 0.1); display: flex; align-items: center; justify-content: center; margin-bottom: 50px; position: relative; }
            .icon-wrapper { z-index: 10; display: flex; align-items: center; justify-content: center; color: var(--primary); transition: transform 0.3s ease; }
            .icon-wrapper.speaking { transform: scale(1.1); }
            .pulse { position: absolute; top: 50%; left: 50%; width: 140px; height: 140px; margin: -70px 0 0 -70px; border-radius: 50%; border: 2px solid var(--primary); animation: pulse-ring 2.5s infinite; opacity: 0; }
            @keyframes pulse-ring { 0% { transform: scale(0.8); opacity: 0.5; } 100% { transform: scale(1.8); opacity: 0; } }
            @keyframes pulse-dot { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }
            .call-btn { padding: 18px 56px; border-radius: 40px; border: none; background: var(--primary); color: white; font-size: 16px; font-weight: 700; cursor: pointer; transition: all 0.3s; box-shadow: 0 8px 20px rgba(99, 102, 241, 0.3); font-family: sans-serif; }
            .call-btn:hover { transform: translateY(-4px); }
            .call-btn.stop { background: #ef4444; }
            .log-box { width: 100%; font-family: sans-serif; font-size: 13px; color: rgba(255, 255, 255, 0.5); text-align: center; margin-top: 24px; opacity: 0.8; }
            .notif-overlay { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(0,0,0,0.6); z-index: 100000; display: none; align-items: center; justify-content: center; backdrop-filter: blur(4px); padding: 16px; box-sizing: border-box; }
            .notif-overlay.visible { display: flex; }
            .notif-modal { width: 100%; max-width: 380px; box-sizing: border-box; background: rgba(30, 41, 59, 0.98); border-radius: 20px; padding: 32px 24px; text-align: center; box-shadow: 0 20px 50px rgba(0,0,0,0.5); border: 1px solid rgba(255,255,255,0.1); backdrop-filter: blur(20px); }
            .notif-icon { width: 64px; height: 64px; border-radius: 50%; margin: 0 auto 24px; display: flex; align-items: center; justify-content: center; font-size: 32px; }
            .notif-title { font-weight: 800; font-size: 22px; color: #ffffff; margin-bottom: 12px; font-family: sans-serif; }
            .notif-msg { font-size: 15px; color: rgba(255, 255, 255, 0.7); line-height: 1.6; font-family: sans-serif; margin-bottom: 10px; }
            .notif-btn { width: 100%; margin-top: 25px; padding: 14px; background: var(--primary); color: white; border: none; border-radius: 12px; cursor: pointer; font-weight: 700; font-family: sans-serif; font-size: 15px; transition: all 0.2s; box-sizing: border-box; }
            .notif-btn:hover { background: var(--primary-hover); transform: translateY(-2px); }
            .handoff-popup { position: fixed; top: 20px; right: 20px; width: 320px; max-width: calc(100vw - 40px); box-sizing: border-box; background: rgba(30, 41, 59, 0.98); border-radius: 12px; box-shadow: 0 10px 30px rgba(0,0,0,0.4); padding: 20px; z-index: 11000; animation: slideInRight 0.3s ease-out; border: 1px solid rgba(255,255,255,0.1); backdrop-filter: blur(10px); }
            @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
            .handoff-title { font-weight: bold; margin-bottom: 8px; font-size: 16px; color: #ffffff; }
            .handoff-msg { font-size: 14px; color: rgba(255, 255, 255, 0.7); margin-bottom: 15px; }
            .handoff-actions { display: flex; gap: 8px; }
            .handoff-btn { flex: 1; padding: 10px; border-radius: 8px; border: none; cursor: pointer; font-weight: 600; }
            .handoff-btn.accept { background: #10b981; color: white; }
            .handoff-btn.decline { background: rgba(255,255,255,0.1); color: white; }
            .handoff-reason { font-size: 13px; color: rgba(255, 255, 255, 0.5); font-style: italic; margin-top: 4px; }
            .handoff-summary { font-size: 13px; color: rgba(255, 255, 255, 0.7); margin-top: 10px; padding: 10px; background: rgba(0,0,0,0.2); border-left: 2px solid var(--primary); border-radius: 4px; line-height: 1.4; }
            .credlix-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(15, 23, 42, 0.85); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 999999; opacity: 0; pointer-events: none; transition: opacity 0.3s ease; padding: 16px; box-sizing: border-box; }
            .credlix-modal-overlay.active { opacity: 1; pointer-events: auto; }
            .credlix-modal-content { background: #1e293b; padding: 36px 28px; border-radius: 20px; border: 1px solid rgba(255,255,255,0.1); box-shadow: 0 25px 50px -12px rgba(0,0,0,0.5); width: 100%; max-width: 400px; box-sizing: border-box; text-align: center; transform: scale(0.95); transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275); font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
            .credlix-modal-overlay.active .credlix-modal-content { transform: scale(1); }
            @keyframes credlix-pulse-ring { 0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); } 70% { transform: scale(1); box-shadow: 0 0 0 15px rgba(16, 185, 129, 0); } 100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); } }
            .credlix-ringing-animation { animation: credlix-pulse-ring 2s infinite; }
            .credlix-btn-success { background-color: #10b981; color: white; border: none; border-radius: 12px; cursor: pointer; transition: 0.2s; font-weight: 700; }
            .credlix-btn-success:hover { background-color: #059669; transform: translateY(-1px); }
            .credlix-btn-danger { background-color: #ef4444; color: white; border: none; border-radius: 12px; cursor: pointer; transition: 0.2s; font-weight: 700; }
            .credlix-btn-danger:hover { background-color: #dc2626; transform: translateY(-1px); }
            .credlix-btn-outline { background: rgba(255,255,255,0.05); color: #fff; border: 1px solid rgba(255,255,255,0.2); border-radius: 12px; cursor: pointer; transition: 0.2s; }
            .credlix-btn-outline.muted { color: #f87171; border-color: #f87171; background: rgba(248, 113, 113, 0.1); }
            .credlix-telephony-icon { width: 80px; height: 80px; background: #6366f1; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; margin: 0 auto 20px auto; position: relative; }
            .credlix-telephony-icon::before { content: ''; position: absolute; top: -10px; left: -10px; right: -10px; bottom: -10px; border: 2px solid #6366f1; border-radius: 50%; animation: credlix-telephony-pulse 2s infinite; opacity: 0; }
            @keyframes credlix-telephony-pulse { 0% { transform: scale(0.9); opacity: 0.8; } 100% { transform: scale(1.4); opacity: 0; } }

            /* Mobile Responsiveness */
            @media (max-width: 768px) {
                .credlix-modal-overlay { padding: 16px; }
                .credlix-modal-content { padding: 24px 16px; border-radius: 16px; max-width: 100%; width: 100%; }
                .panel { width: 100%; max-width: 340px; height: auto; max-height: calc(100vh - 32px); border-radius: 16px; margin: 0 auto; }
                .body { padding: 28px 16px; }
                .status-indicator { margin-bottom: 24px; }
                .call-animation { width: 120px; height: 120px; margin-bottom: 28px; }
                .pulse { width: 120px; height: 120px; margin: -60px 0 0 -60px; }
                .call-btn { padding: 14px 36px; width: 100%; max-width: 240px; }
                .notif-overlay { padding: 16px; }
                .notif-modal { width: 100%; max-width: 100%; padding: 24px 16px; border-radius: 16px; }
                .handoff-popup { right: 12px; left: 12px; width: auto; }
                .widget-toggle { width: 56px; height: 56px; bottom: 16px; right: 16px; }
                .widget-show-btn { bottom: 16px; right: 16px; }
            }
        `;

        shadow.appendChild(style);

        // --- Notification Overlay (scoped to shadow DOM) ---
        const notifOverlay = document.createElement("div");
        notifOverlay.className = "notif-overlay";
        notifOverlay.innerHTML = `<div class="notif-modal"><div class="notif-icon"></div><div class="notif-title"></div><div class="notif-msg"></div><button class="notif-btn">Dismiss</button></div>`;
        notifOverlay.querySelector(".notif-btn").addEventListener("click", () => notifOverlay.classList.remove("visible"));
        shadow.appendChild(notifOverlay);

        // --- Main Widget Panel ---
        const toggle = document.createElement("div");
        toggle.className = "widget-toggle";
        toggle.innerHTML = `<svg viewBox="0 0 24 24"><path d="M6.62,10.79C8.06,13.62 10.38,15.94 13.21,17.38L15.41,15.18C15.69,14.9 16.08,14.82 16.43,14.93C17.55,15.3 18.75,15.5 20,15.5A1,1 0 0,1 21,16.5V20A1,1 0 0,1 20,21A17,17 0 0,1 3,4A1,1 0 0,1 4,3H7.5A1,1 0 0,1 8.5,4C8.5,5.25 8.7,6.45 9.07,7.57C9.18,7.92 9.1,8.31 8.82,8.59L6.62,10.79Z"/></svg>`;

        // [UX] Hide affordance on the floating button (only if enabled)
        let hideBtn = null;
        if (this.allowButtonHide) {
            hideBtn = document.createElement("div");
            hideBtn.className = "toggle-hide-btn";
            hideBtn.innerHTML = "×";
            hideBtn.title = "Hide button";
            toggle.appendChild(hideBtn);
        }

        // [UX] Small pill to restore the button after it's hidden
        const showBtn = document.createElement("div");
        showBtn.className = "widget-show-btn";
        showBtn.innerHTML = `<svg viewBox="0 0 24 24"><path d="M6.62,10.79C8.06,13.62 10.38,15.94 13.21,17.38L15.41,15.18C15.69,14.9 16.08,14.82 16.43,14.93C17.55,15.3 18.75,15.5 20,15.5A1,1 0 0,1 21,16.5V20A1,1 0 0,1 20,21A17,17 0 0,1 3,4A1,1 0 0,1 4,3H7.5A1,1 0 0,1 8.5,4C8.5,5.25 8.7,6.45 9.07,7.57C9.18,7.92 9.1,8.31 8.82,8.59L6.62,10.79Z"/></svg>`;
        showBtn.title = "Show call button";

        const panelOverlay = document.createElement("div");
        panelOverlay.className = "panel-overlay";

        const panel = document.createElement("div");
        panel.className = "panel";
        panel.innerHTML = `
            <div class="header"><h3>${this.chatTitle}</h3><span class="close-btn" style="cursor:pointer; font-size:24px">&times;</span></div>
            <div class="body">
                <div class="status-indicator"><span class="status-dot"></span><span class="status-text">Disconnected</span></div>
                <div class="call-animation"><div class="pulse-container"></div><div class="icon-wrapper"><svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg></div></div>
                <button class="call-btn">${this.callBtnText}</button>
                <div class="log-box">Ready to simulate inbound call</div>
            </div>
        `;
        panelOverlay.appendChild(panel);

        toggle.addEventListener("click", () => panelOverlay.classList.toggle("visible"));
        panel.querySelector(".close-btn").addEventListener("click", () => {
            panelOverlay.classList.remove("visible");
            if (this.socket) this.stopCall();
        });
        panelOverlay.addEventListener("click", (e) => {
            if (e.target === panelOverlay) {
                panelOverlay.classList.remove("visible");
                if (this.socket) this.stopCall();
            }
        });

        // [UX] Hide/show floating button
        if (hideBtn) {
            hideBtn.addEventListener("click", (e) => {
                e.stopPropagation();
                this._hideFloatingButton();
            });
        }
        showBtn.addEventListener("click", () => this._showFloatingButton());

        panel.querySelector(".call-btn").addEventListener("click", () => {
            if (this.socket) this.stopCall(); else this.startCall();
        });

        this.widget = {
            toggle, showBtn, panel, panelOverlay,
            statusDot: panel.querySelector(".status-dot"),
            statusText: panel.querySelector(".status-text"),
            pulseContainer: panel.querySelector(".pulse-container"),
            iconWrapper: panel.querySelector(".icon-wrapper"),
            log: panel.querySelector(".log-box"),
            notif: { overlay: notifOverlay, icon: notifOverlay.querySelector(".notif-icon"), title: notifOverlay.querySelector(".notif-title"), msg: notifOverlay.querySelector(".notif-msg") }
        };

        // --- Ring Modal (scoped to shadow DOM) ---
        const ringModal = document.createElement("div");
        ringModal.className = "credlix-modal-overlay";
        ringModal.innerHTML = `
            <div class="credlix-modal-content">
                <div class="credlix-ring-view">
                    <h3 style="margin-bottom: 5px; color: #10b981; font-weight: 700; letter-spacing: 1px; font-size: 14px; margin-top: 0;">INCOMING CALL</h3>
                    <p class="credlix-ring-customer" style="font-size: 24px; font-weight: 700; margin-bottom: 8px; color: #fff; margin-top: 10px;">Unknown Caller</p>
                    <p class="credlix-ring-reason" style="font-size: 13px; color: rgba(255,255,255,0.5); margin-bottom: 30px;">Live assistance requested</p>
                    <div class="credlix-telephony-icon credlix-ringing-animation" style="background: #10b981;">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor"><path d="M6.62,10.79A17.06,17.06,0,0,0,13.21,17.38L15.41,15.18a1,1,0,0,1,1.07-.24,11.45,11.45,0,0,0,3.52.56,1,1,0,0,1,1,1V20a1,1,0,0,1-1,1A17,17,0,0,1,3,4,1,1,0,0,1,4,3H7.5a1,1,0,0,1,1,1,11.36,11.36,0,0,0,.56,3.52,1,1,0,0,1-.24,1.07Z"/></svg>
                    </div>
                    <button class="credlix-ring-answer-btn credlix-btn-success" style="width: 100%; font-size: 16px; padding: 14px; font-family: inherit;">Answer Call</button>
                    <button class="credlix-ring-reject-btn" style="width: 100%; background: none; border: none; color: rgba(255,255,255,0.4); margin-top: 15px; cursor: pointer; text-decoration: underline; font-size: 13px;">Decline</button>
                </div>
                <div class="credlix-cockpit-view" style="display: none;">
                    <h3 style="margin-bottom: 5px; color: #6366f1; font-weight: 700; letter-spacing: 1px; font-size: 14px; margin-top: 0;">CALL IN PROGRESS</h3>
                    <p class="credlix-cockpit-customer" style="font-size: 24px; font-weight: 700; margin-bottom: 8px; color: #fff; margin-top: 10px;">Active Session</p>
                    <p class="credlix-cockpit-status" style="font-size: 13px; color: #10b981; margin-bottom: 30px; font-weight: 600;">Secure Link Established</p>
                    <div class="credlix-telephony-icon">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor"><path d="M12,11A3,3,0,0,0,9,14V17A3,3,0,0,0,15,17V14A3,3,0,0,0,12,11Z"/><path d="M12,2A10,10,0,1,0,22,12,10,10,0,0,0,12,2Zm1,15a1,1,0,0,1-2,0V14a1,1,0,0,1,2,0Z"/></svg>
                    </div>
                    <div style="display: flex; gap: 12px; flex-direction: column;">
                        <button class="credlix-cockpit-mute-btn credlix-btn-outline" style="padding: 14px; display: flex; align-items: center; justify-content: center; gap: 10px;">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                            Mute
                        </button>
                        <button class="credlix-cockpit-end-btn credlix-btn-danger" style="padding: 14px; display: flex; align-items: center; justify-content: center; gap: 10px;">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12,9c-1.6,0-3.1,0.5-4.4,1.4L4.7,7.5c2.1-1.6,4.7-2.5,7.3-2.5s5.2,0.9,7.3,2.5l-2.9,2.9C15.1,9.5,13.6,9,12,9z M12,17c-1.1,0-2-0.9-2-2v-4h4v4C14,16.1,13.1,17,12,17z"/></svg>
                            End Call
                        </button>
                    </div>
                </div>
            </div>
        `;
        shadow.appendChild(ringModal);

        // --- Connecting Modal (scoped to shadow DOM) ---
        const connectingModal = document.createElement("div");
        connectingModal.className = "credlix-modal-overlay";
        connectingModal.innerHTML = `
            <div class="credlix-modal-content">
                <h3 style="margin-bottom: 5px; color: #818cf8; font-weight: 700; letter-spacing: 1px; font-size: 16px; margin-top: 0;">OUTBOUND CALL INITIATED</h3>
                <p class="credlix-connecting-customer" style="font-size: 22px; font-weight: 600; margin-bottom: 8px; color: #fff; margin-top: 10px;">Connecting...</p>
                <p style="font-size: 13px; color: rgba(255,255,255,0.5); margin-bottom: 25px;">Please wait... it could take 10-20 seconds to establish the connection.</p>
                <div class="credlix-ringing-animation" style="width: 50px; height: 50px; border-radius: 50%; background: #6366f1; margin: 0 auto 15px auto;"></div>
                <button class="credlix-connecting-cancel-btn" style="width: 100%; background: none; border: none; color: rgba(255,255,255,0.4); margin-top: 15px; cursor: pointer; text-decoration: underline; font-size: 13px;">Cancel Call</button>
            </div>
        `;
        shadow.appendChild(connectingModal);

        // --- Cache element references on instance ---
        this.elements = {
            ringModal,
            ringView: ringModal.querySelector(".credlix-ring-view"),
            cockpitView: ringModal.querySelector(".credlix-cockpit-view"),
            ringCustomer: ringModal.querySelector(".credlix-ring-customer"),
            ringReason: ringModal.querySelector(".credlix-ring-reason"),
            ringAnswerBtn: ringModal.querySelector(".credlix-ring-answer-btn"),
            ringRejectBtn: ringModal.querySelector(".credlix-ring-reject-btn"),
            cockpitCustomer: ringModal.querySelector(".credlix-cockpit-customer"),
            cockpitStatus: ringModal.querySelector(".credlix-cockpit-status"),
            cockpitMuteBtn: ringModal.querySelector(".credlix-cockpit-mute-btn"),
            cockpitEndBtn: ringModal.querySelector(".credlix-cockpit-end-btn"),
            connectingModal,
            connectingCustomer: connectingModal.querySelector(".credlix-connecting-customer"),
            connectingCancelBtn: connectingModal.querySelector(".credlix-connecting-cancel-btn"),
        };

        // --- Bind modal event listeners (scoped to this instance) ---
        this.elements.ringAnswerBtn.addEventListener("click", () => {
            if (this._currentIncomingData) {
                this.stopRingSound();
                const btn = this.elements.ringAnswerBtn;
                btn.innerText = "Connecting...";
                btn.style.opacity = "0.7";
                this.answerHandoff(this._currentIncomingData.call_id);
            }
        });

        this.elements.ringRejectBtn.addEventListener("click", () => {
            this.stopRingSound();
            if (this._currentIncomingData && this._currentIncomingData.call_id) {
                if (this.availabilitySocket && this.availabilitySocket.readyState === WebSocket.OPEN) {
                    this.availabilitySocket.send(JSON.stringify({
                        type: "decline_handoff",
                        call_id: this._currentIncomingData.call_id
                    }));
                }
            }
            this._hideRingModal();
        });

        this.elements.cockpitMuteBtn.addEventListener("click", () => {
            if (this.isMuted) this.unmute(); else this.mute();
        });

        this.elements.cockpitEndBtn.addEventListener("click", () => {
            this.stopCall();
        });

        this.elements.connectingCancelBtn.addEventListener("click", () => {
            this.stopCall();
            this._hideConnectingModal();
            this.notify("Cancelled", "Outbound call sequence aborted.", "info");
        });

        this.container = container;
        document.body.appendChild(container);
        if (this.renderWidget) {
            if (this.showFloatingButton) {
                shadow.appendChild(toggle);
                shadow.appendChild(showBtn);
            }
            shadow.appendChild(panelOverlay);
        }
    }

    _hideFloatingButton() {
        if (!this.renderWidget) return;
        this.widget.toggle.classList.add("hidden");
        this.widget.showBtn.classList.add("visible");
        // console.log("[CredlixAgent] Floating button hidden");
    }

    _showFloatingButton() {
        if (!this.renderWidget) return;
        this.widget.toggle.classList.remove("hidden");
        this.widget.showBtn.classList.remove("visible");
        // console.log("[CredlixAgent] Floating button shown");
    }

    _showConnectingModal(phoneNumber, name) {
        if (this.elements) {
            if (this.elements.connectingCustomer) {
                this.elements.connectingCustomer.innerText = name ? `${name} (${phoneNumber})` : phoneNumber;
            }
            if (this.elements.connectingModal) {
                this.elements.connectingModal.classList.add("active");
            }
        }
        // [UX] Ring sound while connecting so the agent hears the call progress
        this.playRingSound();
    }

    _hideConnectingModal() {
        if (this.elements && this.elements.connectingModal) {
            this.elements.connectingModal.classList.remove("active");
        }
        this.stopRingSound();
    }

    _showActiveCockpit(name, phone) {
        this._hideConnectingModal();

        if (this.elements) {
            if (this.elements.ringView) this.elements.ringView.style.display = "none";
            if (this.elements.cockpitView) this.elements.cockpitView.style.display = "block";
            if (this.elements.cockpitCustomer) this.elements.cockpitCustomer.innerText = name || phone || "Active Call";
            if (this.elements.ringModal) this.elements.ringModal.classList.add("active");
        }

        this.isMuted = false;
        this._updateMuteUI();
    }

    _hideActiveCockpit() {
        const modal = this.elements ? this.elements.ringModal : null;
        if (modal) modal.classList.remove("active");
        // Reset views for next time
        setTimeout(() => {
            if (this.elements && this.elements.ringView && this.elements.cockpitView) {
                this.elements.ringView.style.display = "block";
                this.elements.cockpitView.style.display = "none";
            }
        }, 300);
    }

    _showIncomingCallPopup(data) {
        this._hideConnectingModal(); // Transition cleanly from Connecting -> Ringing if outbound
        this._currentIncomingData = data;

        if (this.elements) {
            if (this.elements.ringView) this.elements.ringView.style.display = "block";
            if (this.elements.cockpitView) this.elements.cockpitView.style.display = "none";
            if (this.elements.ringCustomer) this.elements.ringCustomer.innerText = data.customer_name || data.phone_number || "Unknown Caller";
            if (this.elements.ringReason) this.elements.ringReason.innerText = data.reason || "Live assistance requested";

            const btn = this.elements.ringAnswerBtn;
            if (btn) {
                btn.innerText = "Answer Call";
                btn.style.opacity = "1";
                btn.style.pointerEvents = "auto";
            }

            if (this.elements.ringModal) this.elements.ringModal.classList.add("active");
        }
    }

    _hideRingModal() {
        if (this.elements && this.elements.ringModal) {
            this.elements.ringModal.classList.remove("active");
        }
        this._currentIncomingData = null;
    }

    _updateState(state) {
        if (this.onStateChange) this.onStateChange(state);
        if (!this.renderWidget) return;
        const { statusDot, statusText, panel, panelOverlay, pulseContainer, log } = this.widget;
        const btn = panel.querySelector(".call-btn");
        switch (state) {
            case "idle":
                statusDot.classList.remove("active"); statusText.innerText = "Disconnected";
                btn.innerText = this.callBtnText; btn.classList.remove("stop"); btn.disabled = false;
                pulseContainer.innerHTML = "";
                if (panelOverlay) panelOverlay.classList.remove("visible");
                break;
            case "connecting":
                if (panelOverlay) panelOverlay.classList.add("visible");
                btn.innerText = "Connecting..."; btn.disabled = true;
                log.innerText = "Establishing Secure Link..."; break;
            case "active":
                if (panelOverlay) panelOverlay.classList.add("visible");
                statusDot.classList.add("active"); statusText.innerText = "Call in Progress";
                btn.innerText = "End Call"; btn.classList.add("stop"); btn.disabled = false;
                pulseContainer.innerHTML = '<div class="pulse"></div><div class="pulse"></div><div class="pulse"></div>';
                log.innerText = "AI is listening..."; break;
        }
    }

    // -------------------------------------------------------------------------
    // Utils & Auth
    // -------------------------------------------------------------------------

    _decodeToken(token) {
        // Decode JWT payload (base64url) to read claims client-side. This does NOT
        // verify the signature — the server does that. It just lets the SDK derive
        // agent_email/company_id from the same token the server will decode.
        if (!token || typeof token !== "string") return null;
        try {
            const parts = token.split(".");
            if (parts.length !== 3) return null;
            let payload = parts[1];
            // Add base64url padding
            payload += "=".repeat((4 - payload.length % 4) % 4);
            // base64url → base64
            payload = payload.replace(/-/g, "+").replace(/_/g, "/");
            const json = atob(payload);
            return JSON.parse(decodeURIComponent(escape(json)));
        } catch (e) {
            return null;
        }
    }

    async _getToken() {
        if (this.token) return this.token;
        if (!this.apiKey || !this.apiSecret) throw new Error("Credentials missing");
        const res = await fetch(`${this.baseUrl}/api/v1/company-auth/token`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ client_id: this.apiKey, client_secret: this.apiSecret, workflow_id: this.workflowId })
        });
        if (!res.ok) throw new Error("Auth failed.");
        const data = await res.json();
        this.token = data.access_token;
        return this.token;
    }

    async _initSimulation() {
        this.playDialingSound();
        try {
            const res = await fetch(`${this.baseUrl}/api/v1/call/simulate`, {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${this.token}` },
                body: JSON.stringify({
                    phone_number: this.phoneNumber,
                    name: this.userName,
                    direction: "inbound",
                    target_group_id: this.workflowId,
                    context: {
                        page_url: typeof window !== "undefined" ? window.location.href : "",
                        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
                        name: this.userName,
                        customer_name: this.userName,
                        _sdk_source: "CredlixAgent",
                        _sdk_timestamp: new Date().toISOString(),
                        ...(this.config?.context || {})
                    }
                })
            });
            if (!res.ok) throw new Error("Simulation failed.");
            const data = await res.json();
            this.callId = data.call_id; this.companyId = data.company_id;
            return data;
        } catch (err) {
            this.stopDialingSound();
            throw err;
        }
    }

    log(msg) { console.log(`[CredlixAgent] ${msg}`); if (this.renderWidget) this.widget.log.innerText = msg; }

    notify(title, message, type = "success") {
        if (!this.showNotifications) return;
        const { notif } = this.widget;
        if (!notif) return alert(`${title}: ${message}`);
        notif.title.innerText = title; notif.msg.innerText = message;
        notif.icon.className = `notif-icon ${type}`; notif.icon.innerHTML = type === "success" ? "✓" : "⚠";
        notif.overlay.classList.add("visible");
    }

    _startHeartbeat() {
        this._stopHeartbeat();
        this.heartbeatTimer = setInterval(() => {
            if (this.availabilitySocket && this.availabilitySocket.readyState === WebSocket.OPEN) {
                this.availabilitySocket.send(JSON.stringify({ type: "heartbeat" }));
            }
        }, this.heartbeatInterval);
    }

    _stopHeartbeat() { if (this.heartbeatTimer) clearInterval(this.heartbeatTimer); }

    destroy() {
        this.stopCall();
        this.goOffline();
        this.stopRingSound();
        this.stopDialingSound();
        this._stopHeartbeat();
        this._stopUIMonitor();
        if (this.container) {
            this.container.remove();
            this.container = null;
        }
        this.elements = null;
    }
}

if (typeof window !== "undefined") {
    window.CredlixAgent = CredlixAgent;
}

export default CredlixAgent;